import React, { useCallback, useEffect, useMemo, useState } from "react";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import AssignmentIcon from "@mui/icons-material/Assignment";
import baseUrl from "../../../../api/baseUrl";
import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { showSmsResultToast, notifySmsBalanceChanged } from "../../../../utils/sms/smsToast";
import { useDispatch, useSelector } from "react-redux";
import { setPurpose } from "../../../../redux/appointment/appointmentAction";
import { RootState } from "../../../../redux/store";
import { Hourglass } from "react-loader-spinner";
import socket from "../../../../api/socket";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import EventNoteIcon from "@mui/icons-material/EventNote";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_OPTIONS,
  formatQueueFromItem,
  formatPurposeIdsFromCatalog,
  parseQueueInput,
  purposeIdsFromAppointment,
  statusStyles,
} from "../../viewappointment/components/appointmentViewShared";
import {
  UI,
  AppointmentModalOverlay,
  PurposeMultiSelect,
  DurationChips,
  durationChipOptions,
  formatSlotDate,
  formatTimeHm,
  timeToMinutesHm,
  addMinutesToHm,
  toBackendTime,
} from "./appointmentScheduleUi";
import { formatAppointmentDateYmd } from "../calender/newAppointmentForm";

const RESCHEDULE_FORM_ID = "reschedule-appointment-fast-form";
const MIN_DURATION_MINUTES = 5;

function slotMetaForSlot(
  timeSlots: any[],
  slotStart: Date
): { sessionName?: string; roomNumber: string } {
  const day = slotStart.getDay();
  const hours = slotStart.getHours();
  const minutes = slotStart.getMinutes();
  const match = timeSlots.find((interval: any) => {
    const intStart = interval.start;
    const intEnd = interval.end;
    if (interval.day !== day) return false;
    const afterStart =
      hours > intStart.hours || (hours === intStart.hours && minutes >= intStart.minutes);
    const beforeEnd =
      hours < intEnd.hours || (hours === intEnd.hours && minutes < intEnd.minutes);
    return afterStart && beforeEnd;
  });
  if (!match) return { roomNumber: "" };
  const sessionRaw = match?.sessionName;
  const sessionName =
    sessionRaw == null ? undefined : String(sessionRaw).trim() || undefined;
  const roomRaw = match?.roomNumber;
  const roomNumber = roomRaw == null ? "" : String(roomRaw).trim();
  return { sessionName, roomNumber };
}

/** HH:mm for time inputs from DB values (HH:mm:ss, 12h, etc.). */
function normalizeTimeForInput(raw?: string): string {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  const parsed = moment(s, ["HH:mm:ss", "HH:mm", "H:mm:ss", "H:mm", "h:mm A", "h:mm a"], true);
  if (parsed.isValid()) return parsed.format("HH:mm");
  const match = s.match(/^(\d{1,2}):(\d{2})/);
  if (match) return `${match[1].padStart(2, "0")}:${match[2]}`;
  return "";
}

function timeToMinutesFromString(timeString: string): number {
  const t = normalizeTimeForInput(timeString);
  if (!t) return NaN;
  const [hoursStr, minutesStr] = t.split(":");
  const h = parseInt(hoursStr, 10);
  const m = parseInt(minutesStr, 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  return h * 60 + m;
}

function minutesBetweenTimes(start: string, end: string): number {
  const startM = timeToMinutesFromString(start);
  const endM = timeToMinutesFromString(end);
  if (!Number.isFinite(startM) || !Number.isFinite(endM)) return NaN;
  let diff = endM - startM;
  if (diff <= 0) diff += 24 * 60;
  return diff;
}

function resolveIntervalMinutes(
  start: string,
  end: string,
  stored?: unknown
): number {
  const fromTimes = minutesBetweenTimes(start, end);
  const storedN = parseInt(String(stored ?? ""), 10);
  if (Number.isFinite(fromTimes) && fromTimes > 0) {
    if (Number.isFinite(storedN) && storedN > 0 && storedN === fromTimes) {
      return storedN;
    }
    return fromTimes;
  }
  if (Number.isFinite(storedN) && storedN > 0) return storedN;
  return 30;
}

const BASE_DURATION_MINUTES = Array.from({ length: 24 }, (_, i) => (i + 1) * 5);

function durationOptionsForSelect(currentMinutes?: number | string): number[] {
  const set = new Set(BASE_DURATION_MINUTES);
  const n = parseInt(String(currentMinutes ?? ""), 10);
  if (Number.isFinite(n) && n > 0) set.add(n);
  return Array.from(set).sort((a, b) => a - b);
}

function durationOptionsCapped(
  currentMinutes?: number | string,
  maxMinutes?: number | null
): number[] {
  let opts = durationOptionsForSelect(currentMinutes);
  if (maxMinutes != null && Number.isFinite(maxMinutes) && maxMinutes > 0) {
    opts = opts.filter((m) => m <= maxMinutes);
    const cur = parseInt(String(currentMinutes ?? ""), 10);
    if (Number.isFinite(cur) && cur > 0 && !opts.includes(cur)) opts.push(cur);
    opts.sort((a, b) => a - b);
    if (opts.length === 0) opts = [Math.max(5, Math.min(maxMinutes, 5))];
  }
  return opts;
}

function toPickerDate(value: unknown): Date | null {
  if (value == null || value === "") return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return pickerDateFromYmd(value.trim());
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const ymd = formatAppointmentDateYmd(value);
  if (ymd) return pickerDateFromYmd(ymd);
  const m = moment(value as string | number | Date);
  return m.isValid() ? m.toDate() : null;
}

/** Calendar day as YYYY-MM-DD (local), same as New Appointment — avoids UTC day shift. */
function appointmentDateYmd(value: unknown): string {
  const ymd = formatAppointmentDateYmd(value);
  if (ymd) return ymd;
  return moment().format("YYYY-MM-DD");
}

/** Date picker value from YYYY-MM-DD at local noon (stable across timezones). */
function pickerDateFromYmd(ymd: string | null | undefined): Date | null {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(String(ymd).trim())) return null;
  const [y, mo, d] = String(ymd)
    .trim()
    .split("-")
    .map((n) => parseInt(n, 10));
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return new Date(y, mo - 1, d, 12, 0, 0, 0);
}

function slotStartFromYmdAndTime(ymd: string, startTime: string): Date {
  const base = pickerDateFromYmd(ymd) ?? new Date();
  const [h, m] = formatTimeHm(startTime).split(":").map((x) => parseInt(x, 10));
  base.setHours(h || 0, m || 0, 0, 0);
  return base;
}

function isSameCalendarDay(a: unknown, b: unknown): boolean {
  if (a == null || b == null) return false;
  const ma = moment(a as string | Date);
  const mb = moment(b as string | Date);
  return ma.isValid() && mb.isValid() && ma.isSame(mb, "day");
}

function minutesToHHmm(totalMinutes: number): string {
  const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function timeFromEventStart(event: any): string {
  if (event?.start) {
    const m = moment(event.start);
    if (m.isValid()) return m.format("HH:mm");
  }
  return normalizeTimeForInput(event?.data?.appointment?.startTime);
}

function getEventDoctorId(event: any): string {
  const apt = event?.data?.appointment;
  return String(
    event?.resourceId ?? apt?.doctorId ?? apt?.doctor?._id ?? ""
  ).trim();
}

function getEventAppointmentId(event: any): string {
  const apt = event?.data?.appointment;
  return String(apt?.id ?? apt?._id ?? event?.id ?? "").trim();
}

/** Earliest other appointment start (HH:mm) after `afterStart` on the same calendar day. */
function getNextAppointmentStartOnDay(
  events: any[] | undefined,
  params: {
    day: unknown;
    doctorId: unknown;
    afterStart: string;
    excludeAppointmentId?: string;
  }
): string | null {
  if (!events?.length) return null;
  const doctorKey = String(params.doctorId ?? "").trim();
  const afterM = timeToMinutesFromString(params.afterStart);
  if (!Number.isFinite(afterM)) return null;
  const exclude = String(params.excludeAppointmentId ?? "").trim();

  let bestStartM: number | null = null;

  for (const ev of events) {
    if (!ev?.data?.appointment) continue;
    if (!isSameCalendarDay(ev.start, params.day)) continue;

    const evDoctor = getEventDoctorId(ev);
    if (doctorKey && evDoctor && evDoctor !== doctorKey) continue;

    const evId = getEventAppointmentId(ev);
    if (exclude && evId && evId === exclude) continue;

    const startM = timeToMinutesFromString(timeFromEventStart(ev));
    if (!Number.isFinite(startM) || startM <= afterM) continue;

    if (bestStartM === null || startM < bestStartM) bestStartM = startM;
  }

  return bestStartM === null ? null : minutesToHHmm(bestStartM);
}

function clampEndBeforeNextAppointment(
  startTime: string,
  endTime: string,
  nextStart: string | null
): { endTime: string; interval: number; clamped: boolean } {
  const startM = timeToMinutesFromString(startTime);
  let endM = timeToMinutesFromString(endTime);
  if (!Number.isFinite(startM) || !Number.isFinite(endM)) {
    const interval = minutesBetweenTimes(startTime, endTime);
    return {
      endTime: normalizeTimeForInput(endTime) || endTime,
      interval: Number.isFinite(interval) && interval > 0 ? interval : 30,
      clamped: false,
    };
  }
  if (endM <= startM) endM += 24 * 60;

  let clamped = false;
  if (nextStart) {
    let capM = timeToMinutesFromString(nextStart);
    if (Number.isFinite(capM)) {
      if (capM <= startM) capM += 24 * 60;
      if (endM > capM) {
        endM = capM;
        clamped = true;
      }
    }
  }

  let interval = endM - startM;
  if (interval <= 0) interval = 5;

  return { endTime: minutesToHHmm(endM), interval, clamped };
}

function maxDurationMinutesForSlot(
  startTime: string,
  nextStart: string | null
): number | null {
  if (!nextStart) return null;
  const diff = minutesBetweenTimes(startTime, nextStart);
  return Number.isFinite(diff) && diff > 0 ? diff : null;
}

function endTimeExceedsNextAppointment(
  startTime: string,
  endTime: string,
  nextStart: string | null
): boolean {
  if (!nextStart) return false;
  const { endTime: capped, clamped } = clampEndBeforeNextAppointment(
    startTime,
    endTime,
    nextStart
  );
  return (
    clamped &&
    normalizeTimeForInput(capped) !== normalizeTimeForInput(endTime)
  );
}

function appointmentTimesFromItem(item: any, detail?: any) {
  const rStartTime = formatTimeHm(detail?.startTime || item?.startTime);
  const rEndTime = formatTimeHm(detail?.endTime || item?.endTime);
  const fromTimes = minutesBetweenTimes(rStartTime, rEndTime);
  const stored = parseInt(String(item?.interval ?? ""), 10);
  const intervalMinutes =
    Number.isFinite(fromTimes) && fromTimes > 0
      ? fromTimes
      : Number.isFinite(stored) && stored > 0
        ? stored
        : 30;
  const rInterval = String(intervalMinutes);
  const resolvedEnd =
    rEndTime || (rStartTime ? addMinutesToHm(rStartTime, intervalMinutes) : "");
  return { rStartTime, rEndTime: resolvedEnd, rInterval };
}

function appointmentFieldsFromItem(item: any, detail?: any) {
  return {
    ...appointmentTimesFromItem(item, detail),
    queue: formatQueueFromItem(item),
    sessionName: String(item?.sessionName ?? "").trim(),
    roomNumber: String(item?.roomNumber ?? "").trim(),
    appointmentStatus: String(item?.status ?? "").trim(),
  };
}

function formatSmartDate(value: unknown): string {
  if (!value) return "—";
  const m = moment(value);
  if (!m.isValid()) return "—";
  const today = moment().startOf("day");
  const d = m.clone().startOf("day");
  if (d.isSame(today, "day")) return `Today · ${m.format("MMM D")}`;
  if (d.isSame(today.clone().add(1, "day"), "day")) return `Tomorrow · ${m.format("MMM D")}`;
  if (d.isSame(today.clone().subtract(1, "day"), "day")) return `Yesterday · ${m.format("MMM D")}`;
  return m.format("dddd, MMM D");
}

const DROPDOWN_STATUS_VALUES = new Set<string>(
  APPOINTMENT_STATUS_OPTIONS.map((o) => o.value)
);

const ACTION_OPTION_STATUS: Record<string, string> = {
  rescheduletray: "tray",
  cancel: "canceled",
  Completed: "completed",
};

function statusMatchesActionOption(status: string, actionValue: string): boolean {
  const expected = ACTION_OPTION_STATUS[actionValue];
  if (!expected) return false;
  const normalized = String(status ?? "").trim().toLowerCase();
  if (expected === "canceled") {
    return normalized === "canceled" || normalized === "cancelled";
  }
  return normalized === expected;
}

function StatusBadge({ status, className = "" }: { status: string; className?: string }) {
  const key = status || "default";
  const style = statusStyles[key] || statusStyles.default;
  const label = APPOINTMENT_STATUS_LABELS[key] || key || "—";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide capitalize ${style} ${className}`}
    >
      {label}
    </span>
  );
}

function StatusSelect({
  value,
  disabled,
  saving,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  saving?: boolean;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}) {
  const key = value || "default";
  const style = statusStyles[key] || statusStyles.default;
  return (
    <select
      value={value}
      disabled={disabled || saving}
      onChange={onChange}
      aria-label="Appointment status"
      className={`max-w-[10.5rem] shrink-0 px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${style}`}
    >
      {APPOINTMENT_STATUS_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function MetaChip({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 max-w-full rounded-full bg-slate-100/90 px-3 py-1.5 text-xs font-medium text-slate-700">
      <span className="text-slate-400 shrink-0">{icon}</span>
      <span className="truncate">{children}</span>
    </span>
  );
}

/** Read-only visit slot summary (not input-styled). */
function VisitScheduleSummary({
  dateLabel,
  startTime,
  endTime,
  showEndTime = true,
  doctorName,
  durationLabel,
  endTimeHint,
}: {
  dateLabel: string;
  startTime: string;
  endTime?: string;
  /** When false, only start time is shown (end is edited below). */
  showEndTime?: boolean;
  doctorName: string;
  durationLabel?: string;
  endTimeHint?: string;
}) {
  const timeLine =
    showEndTime && endTime && endTime !== "—"
      ? `${startTime} – ${endTime}`
      : startTime;

  return (
    <div className="rounded-2xl border border-slate-200/60 bg-gradient-to-b from-slate-50/80 to-white p-4">
      <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
        {dateLabel}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-slate-900">
        {timeLine}
      </p>
      {endTimeHint ? (
        <p className="mt-0.5 text-sm text-slate-500">{endTimeHint}</p>
      ) : durationLabel ? (
        <p className="mt-0.5 text-sm text-slate-500">{durationLabel}</p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
        <MetaChip icon={<LocalHospitalIcon sx={{ fontSize: 14 }} />}>
          {doctorName}
        </MetaChip>
      </div>
    </div>
  );
}

function VisitHero({
  name,
  initial,
  phone,
  status,
  statusEditable,
  statusSaving,
  onStatusChange,
  timeRange,
  durationLabel,
  smartDate,
  doctor,
  purpose,
  sessionName,
  room,
  queue,
  hasSession,
  onEdit,
  showEdit,
}: {
  name: string;
  initial: string;
  phone?: string;
  status: string;
  statusEditable?: boolean;
  statusSaving?: boolean;
  onStatusChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  timeRange: string;
  durationLabel: string;
  smartDate: string;
  doctor: string;
  purpose: string;
  sessionName?: string;
  room?: string;
  queue?: string | number;
  hasSession: boolean;
  onEdit?: () => void;
  showEdit?: boolean;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)]">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-primary/[0.07] to-transparent"
        aria-hidden
      />
      <div className="relative p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary/80 text-base font-semibold text-white shadow-md shadow-primary/25">
              {initial}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-semibold text-slate-900 truncate">{name}</h3>
                {statusEditable && DROPDOWN_STATUS_VALUES.has(status) && onStatusChange ? (
                  <StatusSelect
                    value={status}
                    saving={statusSaving}
                    onChange={onStatusChange}
                  />
                ) : (
                  <StatusBadge status={status} />
                )}
              </div>
              {phone ? (
                <a
                  href={`tel:${phone.replace(/[\s\-()]/g, "")}`}
                  className="mt-0.5 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary transition-colors md:pointer-events-none"
                  onClick={(e) => {
                    if (window.innerWidth >= 768) e.preventDefault();
                  }}
                >
                  <LocalPhoneIcon sx={{ fontSize: 14 }} />
                  {phone}
                </a>
              ) : null}
            </div>
          </div>
          {showEdit && onEdit ? (
            <button
              type="button"
              onClick={onEdit}
              className={`${UI.btnSecondary} shrink-0 px-3 py-2 text-sm shadow-sm`}
            >
              <EditIcon sx={{ fontSize: 18 }} />
              Edit
            </button>
          ) : null}
        </div>

        <div className="mt-5 border-t border-slate-100 pt-4">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
            {smartDate}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-slate-900">
            {timeRange}
          </p>
          <p className="mt-0.5 text-sm text-slate-500">{durationLabel}</p>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <MetaChip icon={<LocalHospitalIcon sx={{ fontSize: 14 }} />}>{doctor}</MetaChip>
          <MetaChip icon={<AssignmentIcon sx={{ fontSize: 14 }} />}>{purpose}</MetaChip>
          {hasSession && sessionName ? (
            <MetaChip icon={<EventNoteIcon sx={{ fontSize: 14 }} />}>{sessionName}</MetaChip>
          ) : null}
          {hasSession && room ? (
            <MetaChip icon={<MeetingRoomIcon sx={{ fontSize: 14 }} />}>Room {room}</MetaChip>
          ) : null}
          {hasSession && queue != null ? (
            <MetaChip icon={<FormatListNumberedIcon sx={{ fontSize: 14 }} />}>
              Queue #{queue}
            </MetaChip>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ActionOption({
  selected,
  icon,
  label,
  description,
  accent,
  onSelect,
}: {
  selected: boolean;
  icon: React.ReactNode;
  label: string;
  description: string;
  accent: "amber" | "red" | "emerald";
  onSelect: () => void;
}) {
  const accentRing = {
    amber: "border-amber-400/80 ring-amber-500/15 bg-amber-50/50",
    red: "border-red-400/80 ring-red-500/15 bg-red-50/40",
    emerald: "border-emerald-400/80 ring-emerald-500/15 bg-emerald-50/40",
  }[accent];
  const iconBg = {
    amber: selected ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500",
    red: selected ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500",
    emerald: selected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500",
  }[accent];

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group w-full flex items-start gap-3 rounded-2xl border px-4 py-3.5 text-left transition-all duration-200 ${
        selected
          ? `ring-4 ${accentRing}`
          : "border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-sm"
      }`}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors ${iconBg}`}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1 pt-0.5">
        <span className="block text-sm font-semibold text-slate-900">{label}</span>
        <span className="block text-xs leading-relaxed text-slate-500 mt-0.5">{description}</span>
      </span>
    </button>
  );
}

function FormSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-200/60 bg-white p-4 space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
        {hint ? <p className="text-xs text-slate-500 mt-0.5">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

type RescheduleShellProps = {
  title: string;
  slotLine: string;
  trayHint?: string | null;
  loading: boolean;
  saving: boolean;
  patientName: string;
  patientPhone?: string;
  purposes: any;
  formData: any;
  setformData: React.Dispatch<React.SetStateAction<any>>;
  purposeError: boolean;
  setPurposeError: (v: boolean) => void;
  pickDuration: (mins: number) => void;
  durationChipOptions: number[];
  onEndTimeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  formNextStart: string | null;
  hasSession: boolean;
  sessionName: string;
  roomNumber: string;
  onQueueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showScheduleFields: boolean;
  lockedSchedule?: {
    doctorName: string;
    dateLabel: string;
    startTime: string;
    endTime: string;
    showEndTime: boolean;
    durationLabel?: string;
    endTimeHint?: string;
  } | null;
  doctor: any[];
  onFieldChange: (e: any) => void;
  isCalendarSlotLocked: boolean;
  scheduleDate: Date | null;
  onDateChange: (date: Date | null) => void;
  onFindNextSlot?: () => void;
  onBackToEdit?: () => void;
  showBack?: boolean;
  cancelLabel: string;
  saveLabel: string;
  onCancel: () => void;
  onSubmit: () => void;
};

function RescheduleAppointmentShell(props: RescheduleShellProps) {
  const {
    title,
    slotLine,
    trayHint,
    loading,
    saving,
    patientName,
    patientPhone,
    purposes,
    formData,
    setformData,
    purposeError,
    setPurposeError,
    pickDuration,
    durationChipOptions,
    onEndTimeChange,
    formNextStart,
    hasSession,
    sessionName,
    roomNumber,
    onQueueChange,
    showScheduleFields,
    lockedSchedule,
    doctor,
    onFieldChange,
    isCalendarSlotLocked,
    scheduleDate,
    onDateChange,
    onFindNextSlot,
    onBackToEdit,
    showBack,
    cancelLabel,
    saveLabel,
    onCancel,
    onSubmit,
  } = props;

  return (
    <div className={`${UI.modalShell} max-w-lg`}>
      <header className={`${UI.modalHeader} py-3`}>
        <div className="min-w-0 flex-1">
          {showBack && onBackToEdit ? (
            <button
              type="button"
              onClick={onBackToEdit}
              className={`${UI.btnGhost} -ml-1 mb-1 px-2 py-1 text-xs`}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              Back
            </button>
          ) : null}
          <h2
            id="appointment-schedule-title"
            className="text-base font-semibold text-slate-900 tracking-tight"
          >
            {title}
          </h2>
          {trayHint ? (
            <p className="mt-0.5 text-[11px] font-medium text-amber-700">{trayHint}</p>
          ) : null}
          {slotLine && !lockedSchedule ? (
            <p className="mt-1 flex items-start gap-1 text-xs text-slate-600 leading-snug">
              <AccessTimeIcon sx={{ fontSize: 14 }} className="shrink-0 mt-0.5 text-primary" />
              <span className="min-w-0">{slotLine}</span>
            </p>
          ) : null}
          {isCalendarSlotLocked ? (
            <button
              type="button"
              onClick={onCancel}
              className="mt-1.5 text-xs font-medium text-primary hover:underline"
            >
              Pick a different time on the calendar
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
          title="Close (Esc)"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </header>

      <div className={`${UI.modalBody} py-3 space-y-3`}>
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-slate-500">
            <Hourglass visible height="32" width="32" colors={["#6366f1", "#6366f1"]} />
            <p className="text-sm font-medium">Loading appointment…</p>
          </div>
        ) : (
          <form id={RESCHEDULE_FORM_ID} onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-3">
            <div className="rounded-xl border border-slate-200/60 bg-white p-3">
              <label className={`${UI.label} mb-0`}>Patient</label>
              <div className="mt-1.5 flex items-center gap-2">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <PersonIcon sx={{ fontSize: 20 }} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{patientName}</p>
                  {patientPhone ? (
                    <p className="text-xs text-slate-500 truncate">{patientPhone}</p>
                  ) : null}
                </div>
              </div>
            </div>

            {lockedSchedule ? (
              <VisitScheduleSummary
                dateLabel={lockedSchedule.dateLabel}
                startTime={lockedSchedule.startTime}
                endTime={lockedSchedule.endTime}
                showEndTime={lockedSchedule.showEndTime}
                doctorName={lockedSchedule.doctorName}
                durationLabel={
                  lockedSchedule.showEndTime ? lockedSchedule.durationLabel : undefined
                }
                endTimeHint={lockedSchedule.endTimeHint}
              />
            ) : null}

            {showScheduleFields ? (
              <div className="rounded-xl border border-slate-200/60 bg-white p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <label className={`${UI.label} mb-0`}>Schedule</label>
                  {onFindNextSlot ? (
                    <button type="button" onClick={onFindNextSlot} className={`${UI.btnGhost} text-xs py-1`}>
                      <SkipNextIcon sx={{ fontSize: 16 }} />
                      Find slot
                    </button>
                  ) : null}
                </div>
                <select
                  name="rDoctorId"
                  value={formData.rDoctorId ?? ""}
                  onChange={onFieldChange}
                  className={UI.input}
                >
                  {doctor?.map((item: any, i: number) => (
                    <option key={i} value={item?._id ?? item?.id}>
                      Dr. {item?.lastName ?? item?.title}
                    </option>
                  ))}
                </select>
                <div className="relative z-[60] [&_.react-datepicker-wrapper]:w-full [&_input]:w-full [&_input]:px-3 [&_input]:py-2 [&_input]:text-sm [&_input]:bg-white [&_input]:border [&_input]:border-slate-200 [&_input]:rounded-xl">
                  <DatePicker
                    minDate={new Date()}
                    dateFormat="MMM d, yyyy"
                    selected={scheduleDate}
                    onChange={onDateChange}
                    popperProps={{ strategy: "fixed" }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    name="rStartTime"
                    value={formatTimeHm(formData.rStartTime)}
                    onChange={onFieldChange}
                    className={`flex-1 ${UI.input} py-2`}
                  />
                  <span className="text-xs text-slate-400">–</span>
                  <input
                    type="time"
                    name="rEndTime"
                    value={formatTimeHm(formData.rEndTime)}
                    min={formatTimeHm(formData.rStartTime) || undefined}
                    max={formNextStart ?? undefined}
                    onChange={onEndTimeChange}
                    className={`flex-1 ${UI.input} py-2`}
                  />
                </div>
              </div>
            ) : null}

            <div className="rounded-xl border border-slate-200/60 bg-white p-3 space-y-2">
              <label className={`${UI.label} mb-0`}>
                Purpose <span className="text-red-500">*</span>
              </label>
              <PurposeMultiSelect
                purposes={purposes?.purpose}
                value={formData.purposeIds ?? []}
                onChange={(ids) => {
                  setPurposeError(false);
                  setformData((prev: any) => ({ ...prev, purposeIds: ids }));
                }}
                error={purposeError}
              />
              {purposeError ? (
                <p className="text-xs text-red-500">Select at least one purpose</p>
              ) : null}
            </div>

            <div className="rounded-xl border border-slate-200/60 bg-white p-3 space-y-2">
              <label className={`${UI.label} mb-0`}>
                {lockedSchedule ? "End time & duration" : "Duration"}
              </label>
              <DurationChips
                value={formData.rInterval}
                options={durationChipOptions}
                onChange={pickDuration}
              />
              {!showScheduleFields ? (
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-xs text-slate-500 shrink-0">
                    {lockedSchedule ? "End time" : "End"}
                  </span>
                  <input
                    type="time"
                    name="rEndTime"
                    value={formatTimeHm(formData.rEndTime)}
                    min={formatTimeHm(formData.rStartTime) || undefined}
                    max={formNextStart ?? undefined}
                    onChange={onEndTimeChange}
                    className={`flex-1 ${UI.input} py-2`}
                  />
                </div>
              ) : null}
              {formNextStart ? (
                <p className="text-xs text-slate-500">
                  End cannot be later than {formNextStart} (next appointment).
                </p>
              ) : null}
            </div>

            {hasSession ? (
              <div className="rounded-xl border border-slate-200/60 bg-white p-3 space-y-3">
                <div className="flex flex-wrap gap-2">
                  <MetaChip icon={<EventNoteIcon sx={{ fontSize: 14 }} />}>
                    {sessionName || "Session"}
                  </MetaChip>
                  {roomNumber ? (
                    <MetaChip icon={<MeetingRoomIcon sx={{ fontSize: 14 }} />}>
                      Room {roomNumber}
                    </MetaChip>
                  ) : null}
                </div>
                <div>
                  <label htmlFor="reschedule-queue" className={UI.label}>
                    Queue
                  </label>
                  <input
                    id="reschedule-queue"
                    type="text"
                    inputMode="numeric"
                    name="queue"
                    autoComplete="off"
                    placeholder="0"
                    value={formData.queue}
                    onChange={onQueueChange}
                    className={`${UI.input} py-2`}
                  />
                </div>
              </div>
            ) : null}
          </form>
        )}
      </div>

      <footer className={`${UI.modalFooter} py-3`}>
        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading || saving}
            className={`${UI.btnSecondary} px-4 py-2 disabled:opacity-50`}
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            form={RESCHEDULE_FORM_ID}
            disabled={loading || saving}
            className={`${UI.btnPrimary} flex-1 py-2.5 disabled:opacity-50`}
          >
            {saving ? (
              <>
                <Hourglass visible height="20" width="20" colors={["#fff", "#fff"]} />
                Saving…
              </>
            ) : (
              <>
                <SaveIcon sx={{ fontSize: 18 }} />
                {saveLabel}
              </>
            )}
          </button>
        </div>
        <p className="text-[10px] text-center text-slate-400 mt-2">
          Esc to close · Ctrl+Enter to save
        </p>
      </footer>
    </div>
  );
}

function AppointmentsSchedule({
  setappointmentModal,
  appointmentDetail,
  doctor,
  onsave,
  selectReSchedule,
  setSelectReSchedule,
  timeSlots,
  events,
  tray,
  setAppointmentDetail,
  setAppointmentType,
}: any) {
  const [selectedOption, setSelectedOption] = useState<string>("Schedule");
  const [error, seterror] = useState(false);
  const [lodingBtn, setlodingBtn] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const purposes = useSelector((state: RootState) => state.appointment);

  const handleOptionChange = (value: string) => {
    setSelectedOption((prev) => {
      if (prev === value) return "";
      const alignedStatus = ACTION_OPTION_STATUS[value];
      if (alignedStatus) {
        setformData((f) => ({ ...f, appointmentStatus: alignedStatus }));
      }
      return value;
    });
  };

  const subtractTimes = useCallback((startTime: string, endTime: string): number => {
    return minutesBetweenTimes(startTime, endTime);
  }, []);

  const addMinutesToTime = useCallback((time: string, minutesToAdd: number): string => {
    const base = normalizeTimeForInput(time) || "00:00";
    return moment(`2024-01-01T${base}`, "YYYY-MM-DDTHH:mm")
      .add(minutesToAdd, "minutes")
      .format("HH:mm");
  }, []);

  const formatTime = useCallback((dates?: any) => {
    const date = new Date(dates);
    return `${String(date.getHours()).padStart(2, "0")}:${String(
      date.getMinutes()
    ).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
  }, []);

  const slotStart = appointmentDetail?.slots?.[0]
    ? normalizeTimeForInput(formatTime(appointmentDetail.slots[0]))
    : "";
  const slotEnd = appointmentDetail?.slots?.[1]
    ? normalizeTimeForInput(
      formatTime(
        appointmentDetail.slots[appointmentDetail.slots.length - 1]
      )
    )
    : "";
  const slotInterval =
    slotStart && slotEnd
      ? String(resolveIntervalMinutes(slotStart, slotEnd, "30"))
      : "30";

  const [formData, setformData] = useState({
    purposeIds: [] as string[],
    sStartTime: slotStart,
    sEndTime: slotEnd,
    sInterval: slotInterval,
    rInterval: slotInterval,
    sDate: appointmentDateYmd(appointmentDetail?.start),
    rStartTime: slotStart,
    rEndTime: slotEnd,
    rDate: appointmentDateYmd(appointmentDetail?.start),
    sDoctorId: appointmentDetail?.doctorId,
    rDoctorId: appointmentDetail?.doctorId,
    name: appointmentDetail?.name,
    mobileNo: appointmentDetail?.mobileNo,
    patientId: appointmentDetail?.patientId,
    queue: "",
    sessionName: String(appointmentDetail?.sessionName ?? "").trim(),
    roomNumber: String(appointmentDetail?.roomNumber ?? "").trim(),
    appointmentStatus: "",
    walkIn: false,
  });

  const dispatch = useDispatch();

  const getPurpose = useCallback(() => {
    baseUrl
      .get("client/appointment/makeappointment/findAllPurpose")
      .then((response) => {
        dispatch(setPurpose(response.data.data, false));
      })
      .catch((error) => {
        console.log(error);
      });
  }, [dispatch]);

  const loadAppointmentData = useCallback(() => {
    const appointmentId = appointmentDetail?.appointmentId;
    const action = appointmentDetail?.action;
    if (!appointmentId) return;

    if (action === "Reschedule") {
      setSelectedOption("Reschedule");
    } else if (action === "Completed") {
      setSelectedOption("");
    } else {
      return;
    }

    setAppointmentLoading(true);
    baseUrl
      .get("appointment/findAppointmentById", {
        headers: { appointmentid: appointmentId },
      })
      .then((response) => {
        const item = response.data.data;
        const doctorId =
          appointmentDetail?.doctorId ??
          item?.doctorId?._id ??
          item?.doctorId;

        if (action === "Reschedule") {
          const slotDateYmd = appointmentDetail?.start
            ? appointmentDateYmd(appointmentDetail.start)
            : appointmentDateYmd(item?.date);
          setformData((prev) => ({
            ...prev,
            purposeIds: purposeIdsFromAppointment(item),
            name: item?.patientId?.fullName,
            mobileNo: item?.patientId?.mobileNo,
            patientId: item?.patientId?._id ?? item?.patientId,
            rDate: slotDateYmd,
            ...appointmentFieldsFromItem(item, appointmentDetail),
            rDoctorId: doctorId,
          }));
          return;
        }

        setformData((prev) => ({
          ...prev,
          purposeIds: purposeIdsFromAppointment(item),
          ...appointmentFieldsFromItem(item),
          rDate: appointmentDateYmd(item?.date),
          rDoctorId: doctorId,
          name: item?.patientId?.fullName,
          mobileNo: item?.patientId?.mobileNo,
          patientId: item?.patientId?._id ?? item?.patientId,
        }));
      })
      .catch((error) => {
        console.log(error);
        toast.error("Could not load appointment details");
      })
      .finally(() => setAppointmentLoading(false));
  }, [appointmentDetail]);

  useEffect(() => {
    if (purposes?.purposeIsEMpty) getPurpose();
  }, [getPurpose, purposes?.purposeIsEMpty]);

  useEffect(() => {
    loadAppointmentData();
  }, [loadAppointmentData]);

  const excludeAppointmentId = appointmentDetail?.appointmentId;

  const resolveNextStart = useCallback(
    (prefix: "s" | "r", prev: typeof formData) => {
      const date = prefix === "s" ? prev.sDate : prev.rDate;
      const doctorId =
        prefix === "s"
          ? prev.sDoctorId || appointmentDetail?.doctorId
          : prev.rDoctorId;
      const start = prefix === "s" ? prev.sStartTime : prev.rStartTime;
      return getNextAppointmentStartOnDay(events, {
        day: date,
        doctorId,
        afterStart: start,
        excludeAppointmentId: prefix === "r" ? excludeAppointmentId : undefined,
      });
    },
    [events, excludeAppointmentId, appointmentDetail?.doctorId]
  );

  const applyIntervalChange = useCallback(
    (prefix: "s" | "r", prev: typeof formData, intervalMinutes: number) => {
      const startKey = prefix === "s" ? "sStartTime" : "rStartTime";
      const endKey = prefix === "s" ? "sEndTime" : "rEndTime";
      const intervalKey = prefix === "s" ? "sInterval" : "rInterval";
      const start = prev[startKey];
      const proposedEnd = addMinutesToTime(start, intervalMinutes);
      const nextStart = resolveNextStart(prefix, prev);
      const { endTime, interval, clamped } = clampEndBeforeNextAppointment(
        start,
        proposedEnd,
        nextStart
      );
      if (clamped && nextStart) {
        toast.info(
          `End time capped at ${endTime} — next appointment starts at ${nextStart}`
        );
      }
      return {
        ...prev,
        [intervalKey]: String(interval),
        [endKey]: endTime,
      };
    },
    [addMinutesToTime, resolveNextStart]
  );

  const applyEndTimeChange = useCallback(
    (prefix: "s" | "r", prev: typeof formData, endVal: string) => {
      const startKey = prefix === "s" ? "sStartTime" : "rStartTime";
      const endKey = prefix === "s" ? "sEndTime" : "rEndTime";
      const intervalKey = prefix === "s" ? "sInterval" : "rInterval";
      const start = prev[startKey];
      const nextStart = resolveNextStart(prefix, prev);
      const { endTime, interval, clamped } = clampEndBeforeNextAppointment(
        start,
        endVal,
        nextStart
      );
      if (clamped && nextStart) {
        toast.info(
          `End time capped at ${endTime} — next appointment starts at ${nextStart}`
        );
      }
      return {
        ...prev,
        [endKey]: endTime,
        [intervalKey]: String(interval),
      };
    },
    [resolveNextStart]
  );

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "sInterval") {
      const mins = parseInt(value, 10);
      if (!Number.isFinite(mins) || mins <= 0) return;
      setformData((prev) => applyIntervalChange("s", prev, mins));
    } else if (name === "rInterval") {
      const mins = parseInt(value, 10);
      if (!Number.isFinite(mins) || mins <= 0) return;
      setformData((prev) => applyIntervalChange("r", prev, mins));
    } else if (name === "sEndTime") {
      const endVal = normalizeTimeForInput(value);
      setformData((prev) => applyEndTimeChange("s", prev, endVal));
    } else if (name === "rEndTime") {
      const endVal = normalizeTimeForInput(value);
      setformData((prev) => applyEndTimeChange("r", prev, endVal));
    } else if (name === "queue") {
      const digits = String(value).replace(/\D/g, "");
      setformData((prevFormData: any) => ({ ...prevFormData, queue: digits }));
    } else if (name === "sDate" || name === "rDate") {
      if (name === "rDate" && appointmentDetail?.slotPickedOnCalendar) return;
      setformData((prevFormData: any) => ({
        ...prevFormData,
        [name]: appointmentDateYmd(value),
      }));
    } else if (
      (appointmentDetail?.slotPickedOnCalendar ||
        (selectedOption === "Reschedule" &&
          appointmentDetail?.action === "Completed" &&
          !appointmentDetail?.fromRescheduleTray)) &&
      (name === "rDoctorId" || name === "rStartTime" || name === "rDate")
    ) {
      return;
    } else {
      setformData((prevFormData: any) => ({ ...prevFormData, [name]: value }));
    }
  };

  let effectiveSessionName = String(formData.sessionName ?? "").trim();
  if (
    !effectiveSessionName &&
    (selectedOption === "Schedule" || selectedOption === "Reschedule")
  ) {
    const datePart =
      selectedOption === "Schedule" ? formData.sDate : formData.rDate;
    const startPart =
      selectedOption === "Schedule" ? formData.sStartTime : formData.rStartTime;
    if (datePart && startPart) {
      const ymd = appointmentDateYmd(datePart);
      const slotDate = slotStartFromYmdAndTime(ymd, startPart);
      const meta = slotMetaForSlot(timeSlots || [], slotDate);
      effectiveSessionName = String(meta.sessionName ?? "").trim();
    }
  }
  const hasSessionQueue = effectiveSessionName !== "";
  const fieldsEditable =
    selectedOption === "Reschedule" || selectedOption === "Schedule";
  const purposeEditable = fieldsEditable;
  const queueEditable = hasSessionQueue && fieldsEditable;
  const purposeDisplayName = useMemo(
    () => formatPurposeIdsFromCatalog(formData.purposeIds, purposes?.purpose),
    [purposes?.purpose, formData.purposeIds]
  );

  const handleStatusDropdownChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const next = e.target.value;
      const appointmentId = appointmentDetail?.appointmentId;
      if (!next || !appointmentId || next === formData.appointmentStatus) return;

      const prevStatus = formData.appointmentStatus;
      const queueNum = parseQueueInput(formData.queue) ?? 0;

      setformData((prev) => ({ ...prev, appointmentStatus: next }));
      if (
        selectedOption &&
        !statusMatchesActionOption(next, selectedOption)
      ) {
        setSelectedOption("");
      }
      setStatusSaving(true);

      socket.emit("allAppointmentStatus", {
        appointmentid: appointmentId,
        status: next,
        queue: queueNum,
        roomNumber: formData.roomNumber || undefined,
        sessionName: formData.sessionName || undefined,
      });

      if (next === "ongoing" && formData.queue !== "" && formData.queue != null) {
        try {
          localStorage.setItem(
            "nextQueue",
            String(Math.max(0, Math.floor(Number(formData.queue))))
          );
        } catch {
          /* ignore quota / private mode */
        }
      }

      baseUrl
        .patch(
          "/appointment/statusAppointmentById",
          {},
          { headers: { appointmentid: appointmentId, status: next } }
        )
        .then(() => onsave())
        .catch((err) => {
          console.error(err);
          setformData((prev) => ({ ...prev, appointmentStatus: prevStatus }));
          toast.error("Failed to update status");
        })
        .finally(() => setStatusSaving(false));
    },
    [
      appointmentDetail?.appointmentId,
      formData.appointmentStatus,
      formData.queue,
      formData.roomNumber,
      onsave,
      selectedOption,
    ]
  );

  const patchQueueForAppointment = useCallback(
    (appointmentId: string, statusHeader = "appointment") => {
      if (!hasSessionQueue) return Promise.resolve();
      const parsed = parseQueueInput(formData.queue);
      if (parsed === null) {
        toast.error("Queue must be a non-negative number");
        return Promise.reject(new Error("invalid queue"));
      }
      return baseUrl
        .patch(
          "/appointment/queueAppointmentById",
          { queue: parsed },
          { headers: { appointmentid: appointmentId } }
        )
        .then(() => {
          socket.emit("allAppointmentStatus", {
            appointmentid: appointmentId,
            status: statusHeader,
            queue: parsed,
            roomNumber: formData.roomNumber || undefined,
            sessionName: formData.sessionName || undefined,
          });
        });
    },
    [formData.queue, formData.roomNumber, hasSessionQueue]
  );

  const handleSave = () => {
    seterror(false);
    setlodingBtn(true);
    if (selectedOption === "") {
      toast.error("select an action");
      setlodingBtn(false);
      return;
    }
    if (selectedOption === "Schedule") {
      if (!formData.purposeIds?.length) {
        seterror(true);
        setlodingBtn(false);
        return;
      }
      const slotStartDate = new Date(`${formData.sDate}T${formData.sStartTime}`);
      const slotMeta = slotMetaForSlot(timeSlots || [], slotStartDate);
      const hasSession = String(slotMeta.sessionName ?? "").trim() !== "";
      if (hasSession) {
        const parsedQueue = parseQueueInput(formData.queue);
        if (parsedQueue === null) {
          toast.error("Queue must be a non-negative number");
          setlodingBtn(false);
          return;
        }
      }

      const scheduleNextStart = resolveNextStart("s", formData);
      if (
        endTimeExceedsNextAppointment(
          formData.sStartTime,
          formData.sEndTime,
          scheduleNextStart
        )
      ) {
        toast.error(
          scheduleNextStart
            ? `End time cannot be later than ${scheduleNextStart} (next appointment on this day).`
            : "End time is invalid for this slot."
        );
        setlodingBtn(false);
        return;
      }

      const datas: Record<string, any> = {
        doctorId: appointmentDetail.doctorId,
        patientId: appointmentDetail?.patientId,
        activeTime: appointmentDetail?.activeTime,
        date: formData.sDate,
        startTime: formData.sStartTime,
        endTime: formData.sEndTime,
        interval: formData.sInterval,
        purpose: formData.purposeIds,
        ...(hasSession
          ? {
            sessionName: slotMeta.sessionName,
            queue: parseQueueInput(formData.queue) ?? 0,
          }
          : {}),
        ...(slotMeta.roomNumber ? { roomNumber: slotMeta.roomNumber } : {}),
        ...(formData.walkIn ? { status: "walkin" } : {}),
      };
      const dataSocket = {
        doctorId: appointmentDetail.doctorId,
        patientId: {
          _id: appointmentDetail?.patientId,
          fullName: formData?.name,
        },
        activeTime: appointmentDetail?.activeTime,
        date: formData.sDate,
        startTime: formData.sStartTime,
        endTime: formData.sEndTime,
        interval: formData.sInterval,
        purpose: formData.purposeIds,
        purposeId: {
          purposeName: formatPurposeIdsFromCatalog(
            formData.purposeIds,
            purposes?.purpose
          ),
        },
        ...(hasSession
          ? {
            sessionName: slotMeta.sessionName,
            queue: parseQueueInput(formData.queue) ?? 0,
          }
          : {}),
        ...(slotMeta.roomNumber ? { roomNumber: slotMeta.roomNumber } : {}),
        ...(formData.walkIn ? { status: "walkin" } : {}),
      };
      socket.emit("newAppointment", dataSocket);
      baseUrl
        .post("/appointment/createAppointment", datas)
        .then((response) => {
          showSmsResultToast(response.data?.sms);
          notifySmsBalanceChanged();
          setappointmentModal(false);
          setSelectedOption("Schedule");
          onsave(datas);
          setlodingBtn(false);
        })
        .catch((error) => {
          console.log(error);
          setlodingBtn(false);
        });
    } else if (selectedOption === "rescheduletray") {
      baseUrl
        .patch(
          "/appointment/statusAppointmentById",
          {},
          {
            headers: {
              appointmentid: appointmentDetail?.appointmentId,
              status: "tray",
            },
          }
        )
        .then((response) => {
          setlodingBtn(false);
          onsave();
          setappointmentModal(false);
        })
        .catch((error) => {
          setlodingBtn(false);
          console.log(error);
        });
    } else if (selectedOption === "Reschedule") {
      if (!formData.purposeIds?.length) {
        seterror(true);
        setlodingBtn(false);
        return;
      }
      if (hasSessionQueue && parseQueueInput(formData.queue) === null) {
        toast.error("Queue must be a non-negative number");
        setlodingBtn(false);
        return;
      }

      const rescheduleNextStart = resolveNextStart("r", formData);
      if (
        endTimeExceedsNextAppointment(
          formData.rStartTime,
          formData.rEndTime,
          rescheduleNextStart
        )
      ) {
        toast.error(
          rescheduleNextStart
            ? `End time cannot be later than ${rescheduleNextStart} (next appointment on this day).`
            : "End time is invalid for this slot."
        );
        setlodingBtn(false);
        return;
      }

      const rStartNorm = formatTimeHm(formData.rStartTime);
      const rEndNorm = formatTimeHm(formData.rEndTime);
      const startM = timeToMinutesHm(rStartNorm);
      const endM = timeToMinutesHm(rEndNorm);
      if (!Number.isFinite(startM) || !Number.isFinite(endM) || endM <= startM) {
        toast.warning("End time must be after start time");
        setlodingBtn(false);
        return;
      }
      const intervalMinutes = endM - startM;
      const dateYmd = appointmentDateYmd(formData.rDate);

      const rMeta = slotMetaForSlot(
        timeSlots || [],
        slotStartFromYmdAndTime(dateYmd, rStartNorm)
      );
      const datas: Record<string, any> = {
        doctorId: formData.rDoctorId,
        patientId: formData?.patientId,
        date: dateYmd,
        startTime: toBackendTime(rStartNorm),
        endTime: toBackendTime(rEndNorm),
        interval: String(intervalMinutes),
        purpose: formData.purposeIds,
      };
      if (rMeta.roomNumber) datas.roomNumber = rMeta.roomNumber;
      if (rMeta.sessionName) datas.sessionName = rMeta.sessionName;
      baseUrl
        .patch("/appointment/reschedule", datas, {
          headers: {
            appointmentid: appointmentDetail?.appointmentId,
            status: "appointment",
          },
        })
        .then((response) => {
          const afterQueue = () => {
            // If from reschedule tray, move to next appointment in tray
            if (appointmentDetail?.fromRescheduleTray && setSelectReSchedule && tray && tray.length > 0) {
              // Find current appointment index
              const currentIndex = tray.findIndex((item: any) => item._id === appointmentDetail?.appointmentId);

              if (currentIndex !== -1 && currentIndex < tray.length - 1) {
                // Move to next appointment in tray
                const nextAppointment = tray[currentIndex + 1];
                setSelectReSchedule(nextAppointment._id);

                // Fetch next appointment data
                baseUrl
                  .get("appointment/findAppointmentById", {
                    headers: { appointmentid: nextAppointment._id },
                  })
                  .then((response) => {
                    const item = response.data.data;

                    // Update appointment detail for next appointment
                    setAppointmentType("Reschedule");
                    setAppointmentDetail({
                      action: "Reschedule",
                      appointmentId: nextAppointment._id,
                      fromRescheduleTray: true,
                    });

                    // Populate form with next appointment data
                    setformData((prev: any) => ({
                      ...prev,
                      purposeIds: purposeIdsFromAppointment(item),
                      name: item?.patientId?.fullName,
                      mobileNo: item?.patientId?.mobileNo,
                      patientId: item?.patientId?._id ?? item?.patientId,
                      rDate: appointmentDateYmd(item?.date),
                      ...appointmentFieldsFromItem(item),
                      rDoctorId: item?.doctorId,
                    }));

                    setlodingBtn(false);
                    toast.success("Appointment rescheduled! Moving to next appointment...");
                  })
                  .catch((error) => {
                    console.error("Error fetching next appointment:", error);
                    setlodingBtn(false);
                    toast.error("Failed to load next appointment");
                  });

                // Refresh appointments list
                onsave();
              } else {
                // No more appointments, clear selection and close
                setSelectReSchedule("");
                onsave();
                setlodingBtn(false);
                setappointmentModal(false);
                toast.success("All appointments rescheduled!");
              }
            } else {
              if (appointmentDetail?.fromRescheduleTray && setSelectReSchedule) {
                setSelectReSchedule("");
              }
              onsave();
              setlodingBtn(false);
              setappointmentModal(false);
              toast.success(
                `Rescheduled to ${formatSmartDate(formData.rDate)} at ${normalizeTimeForInput(formData.rStartTime)}`
              );
            }
          };

          return patchQueueForAppointment(
            appointmentDetail?.appointmentId,
            "appointment"
          )
            .then(afterQueue)
            .catch((err) => {
              console.log(err);
              setlodingBtn(false);
            });
        })
        .catch((error) => {
          console.log(error);
          setlodingBtn(false);
        });
    } else if (selectedOption === "Completed") {
      baseUrl
        .patch(
          "/appointment/completeAppointment",
          {},
          { headers: { appointmentid: appointmentDetail?.appointmentId } }
        )
        .then((response) => {
          setappointmentModal(false);
          setSelectedOption("Schedule");
          onsave();
          setlodingBtn(false);
        })
        .catch((error) => {
          console.log(error);
          setlodingBtn(false);
        });
    } else if (selectedOption === "cancel") {
      baseUrl
        .patch(
          "/appointment/cancelAppointment",
          {},
          { headers: { appointmentid: appointmentDetail?.appointmentId } }
        )
        .then((response) => {
          setappointmentModal(false);
          onsave();
          setSelectedOption("Schedule");
          setlodingBtn(false);
        })
        .catch((error) => {
          console.log(error);
          setlodingBtn(false);
        });
    }
  };

  const isExistingAppointment =
    appointmentDetail?.action === "Completed" && appointmentDetail?.appointmentId;

  const statusEditable =
    isExistingAppointment &&
    !appointmentDetail?.fromRescheduleTray &&
    Boolean(appointmentDetail?.appointmentId);

  const isInPlaceVisitEdit =
    selectedOption === "Reschedule" &&
    isExistingAppointment &&
    !appointmentDetail?.fromRescheduleTray;

  /** Date/time/doctor come from a calendar slot click — do not change in the modal. */
  const isCalendarSlotLocked =
    selectedOption === "Reschedule" &&
    Boolean(appointmentDetail?.slotPickedOnCalendar);

  const isTrayReschedule = Boolean(appointmentDetail?.fromRescheduleTray);
  const isTrayConfirmStep = isTrayReschedule && isCalendarSlotLocked;

  const trayProgress = useMemo(() => {
    if (!tray?.length || !appointmentDetail?.appointmentId) return null;
    const idx = tray.findIndex(
      (item: any) => String(item?._id) === String(appointmentDetail.appointmentId)
    );
    if (idx < 0) return null;
    return { current: idx + 1, total: tray.length };
  }, [tray, appointmentDetail?.appointmentId]);

  const scheduleDate = pickerDateFromYmd(appointmentDateYmd(formData.sDate));
  const rescheduleDate = pickerDateFromYmd(appointmentDateYmd(formData.rDate));

  const scheduleNextStart = useMemo(
    () =>
      getNextAppointmentStartOnDay(events, {
        day: formData.sDate,
        doctorId: formData.sDoctorId || appointmentDetail?.doctorId,
        afterStart: formData.sStartTime,
      }),
    [
      events,
      formData.sDate,
      formData.sDoctorId,
      formData.sStartTime,
      appointmentDetail?.doctorId,
    ]
  );

  const rescheduleNextStart = useMemo(
    () =>
      getNextAppointmentStartOnDay(events, {
        day: formData.rDate,
        doctorId: formData.rDoctorId,
        afterStart: formData.rStartTime,
        excludeAppointmentId,
      }),
    [events, formData.rDate, formData.rDoctorId, formData.rStartTime, excludeAppointmentId]
  );

  const formNextStart =
    selectedOption === "Schedule"
      ? scheduleNextStart
      : selectedOption === "Reschedule"
        ? rescheduleNextStart
        : null;

  const formMaxDuration =
    selectedOption === "Schedule"
      ? maxDurationMinutesForSlot(formData.sStartTime, scheduleNextStart)
      : selectedOption === "Reschedule"
        ? maxDurationMinutesForSlot(formData.rStartTime, rescheduleNextStart)
        : null;

  const showAppointmentSummary =
    isExistingAppointment &&
    !appointmentDetail?.fromRescheduleTray &&
    selectedOption !== "Reschedule" &&
    selectedOption !== "Schedule";

  const doctorDisplayName = (() => {
    const doctorKey = String(formData.rDoctorId ?? "");
    const d = doctor?.find(
      (x: any) => String(x._id ?? x.id) === doctorKey
    );
    if (!d) return "—";
    const name = [d.title, d.firstName, d.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return name ? `Dr. ${name}` : "—";
  })();

  const lockedScheduleForForm =
    isInPlaceVisitEdit || isCalendarSlotLocked
      ? {
          doctorName: doctorDisplayName,
          dateLabel: formatSmartDate(formData.rDate),
          startTime: formatTimeHm(formData.rStartTime) || "—",
          endTime: formatTimeHm(formData.rEndTime) || "—",
          showEndTime: isCalendarSlotLocked && !isInPlaceVisitEdit,
          durationLabel: formData.rInterval
            ? `${formData.rInterval} minute visit`
            : undefined,
          endTimeHint: isInPlaceVisitEdit
            ? "Start time is fixed · change end time below"
            : undefined,
        }
      : null;

  const timeSummaryDisplay =
    formData.rStartTime && formData.rEndTime
      ? `${normalizeTimeForInput(formData.rStartTime)} – ${normalizeTimeForInput(formData.rEndTime)}`
      : "—";

  const saveDisabled =
    lodingBtn || (showAppointmentSummary && !selectedOption);

  const handleBackFromEdit = () => {
    setSelectedOption("");
    seterror(false);
  };

  const handleCancel = () => {
    if (isInPlaceVisitEdit) {
      handleBackFromEdit();
      return;
    }
    setSelectedOption("Schedule");
    setappointmentModal(false);
  };

  const handleEditAppointment = () => {
    setSelectedOption("Reschedule");
    seterror(false);
  };

  const handleMoveToNext = () => {
    if (!timeSlots || timeSlots.length === 0) {
      toast.error("No available time slots found");
      return;
    }

    const now = new Date();
    const currentDate =
      pickerDateFromYmd(appointmentDateYmd(formData.rDate)) ?? new Date();
    const interval = parseInt(formData.rInterval) || 30;

    // Start from today or selected date
    let checkDate = new Date(currentDate);
    if (checkDate < now) {
      checkDate = new Date(now);
      checkDate.setHours(0, 0, 0, 0);
    }

    // Try to find next available slot within next 30 days
    for (let dayOffset = 0; dayOffset < 30; dayOffset++) {
      const checkDay = new Date(checkDate);
      checkDay.setDate(checkDate.getDate() + dayOffset);
      const dayOfWeek = checkDay.getDay();

      // Find working hours for this day
      const dayWorkingHours = timeSlots.find((slot: any) => slot.day === dayOfWeek);

      if (!dayWorkingHours) continue;

      const startHour = dayWorkingHours.start.hours;
      const startMinute = dayWorkingHours.start.minutes;
      const endHour = dayWorkingHours.end.hours;
      const endMinute = dayWorkingHours.end.minutes;

      // Check if it's today - start from current time or working hours start
      let startCheckHour = startHour;
      let startCheckMinute = startMinute;

      if (dayOffset === 0) {
        const currentHour = now.getHours();
        const currentMin = now.getMinutes();
        if (currentHour > startHour || (currentHour === startHour && currentMin >= startMinute)) {
          startCheckHour = currentHour;
          startCheckMinute = Math.ceil(currentMin / 5) * 5; // Round up to nearest 5 minutes
          if (startCheckMinute >= 60) {
            startCheckHour++;
            startCheckMinute = 0;
          }
        }
      }

      // Check available slots for this day
      for (let hour = startCheckHour; hour < endHour || (hour === endHour && startCheckMinute < endMinute); hour++) {
        const startMin = hour === startCheckHour ? startCheckMinute : 0;
        const endMin = hour === endHour ? endMinute : 60;

        for (let minute = startMin; minute < endMin; minute += 5) {
          const slotStart = new Date(checkDay);
          slotStart.setHours(hour, minute, 0, 0);

          const slotEnd = new Date(slotStart);
          slotEnd.setMinutes(slotEnd.getMinutes() + interval);

          // Check if this slot overlaps with existing events
          const isOverlapping = events?.some((event: any) => {
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);
            return slotStart < eventEnd && slotEnd > eventStart;
          });

          if (!isOverlapping) {
            // Found available slot
            const startTimeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
            const endTime = new Date(slotStart);
            endTime.setMinutes(endTime.getMinutes() + interval);
            const endTimeStr = `${String(endTime.getHours()).padStart(2, '0')}:${String(endTime.getMinutes()).padStart(2, '0')}`;

            setformData((prev: any) => ({
              ...prev,
              rDate: appointmentDateYmd(checkDay),
              rStartTime: startTimeStr,
              rEndTime: endTimeStr,
              rInterval: interval.toString(),
            }));

            toast.success(`Moved to next available slot: ${moment(checkDay).format('MMM DD, YYYY')} at ${startTimeStr}`);
            return;
          }
        }
      }
    }

    toast.error("No available time slots found in the next 30 days");
  };

  const actionOptions = [
    {
      value: "rescheduletray",
      label: "Put in reschedule tray",
      description: "Move to tray and reschedule later",
      icon: <PendingActionsIcon sx={{ fontSize: 20 }} />,
      color: "amber" as const,
    },
    {
      value: "cancel",
      label: "Cancel appointment",
      description: "Mark this appointment as canceled",
      icon: <CancelIcon sx={{ fontSize: 20 }} />,
      color: "red" as const,
    },
    {
      value: "Completed",
      label: "Mark as completed",
      description: "Close out this visit as completed",
      icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
      color: "emerald" as const,
    },
  ];

  const smartDate = formatSmartDate(formData.rDate);
  const visitDurationLabel = formData.rInterval
    ? `${formData.rInterval} minute visit`
    : "Duration not set";
  const queueDisplay =
    formData.queue !== "" && formData.queue != null ? formData.queue : "0";

  const headerTitle = showAppointmentSummary
    ? "Visit"
    : isTrayConfirmStep
      ? "Confirm new time"
      : selectedOption === "Schedule"
        ? "New appointment"
        : isInPlaceVisitEdit
          ? "Edit visit"
          : "Reschedule";

  const headerEyebrow = isTrayConfirmStep
    ? trayProgress
      ? `Reschedule · ${trayProgress.current} of ${trayProgress.total}`
      : "Reschedule"
    : showAppointmentSummary
      ? "Scheduled visit"
      : "Appointment";

  const cancelButtonLabel = isTrayConfirmStep
    ? "Back to calendar"
    : isInPlaceVisitEdit
      ? "Back"
      : "Cancel";

  const saveButtonLabel = (() => {
    if (selectedOption === "Reschedule" && isTrayConfirmStep) return "Confirm reschedule";
    if (selectedOption === "Reschedule" && isInPlaceVisitEdit) return "Save edits";
    if (selectedOption === "Reschedule" && isExistingAppointment) return "Save changes";
    if (showAppointmentSummary && selectedOption) return "Apply action";
    return "Save";
  })();

  const selectedActionLabel = actionOptions.find((o) => o.value === selectedOption)?.label;

  const pickRescheduleDuration = useCallback((mins: number) => {
    setformData((prev) => {
      const start = formatTimeHm(prev.rStartTime);
      return {
        ...prev,
        rStartTime: start,
        rInterval: String(mins),
        rEndTime: addMinutesToHm(start, mins),
      };
    });
  }, []);

  const handleRescheduleEndTime = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const endHm = formatTimeHm(e.target.value);
      const startM = timeToMinutesHm(formData.rStartTime);
      const endM = timeToMinutesHm(endHm);
      if (!Number.isFinite(startM) || !Number.isFinite(endM) || endM <= startM) {
        toast.warning("End time must be after start time");
        const mins = Math.max(
          MIN_DURATION_MINUTES,
          parseInt(String(formData.rInterval), 10) || MIN_DURATION_MINUTES
        );
        setformData((prev) => ({
          ...prev,
          rEndTime: addMinutesToHm(prev.rStartTime, mins),
          rInterval: String(mins),
        }));
        return;
      }
      setformData((prev) => ({
        ...prev,
        rEndTime: endHm,
        rInterval: String(endM - startM),
      }));
    },
    [formData.rStartTime, formData.rInterval]
  );

  const rescheduleDurationChipOptions = useMemo(
    () => durationChipOptions(formData.rInterval),
    [formData.rInterval]
  );

  const rescheduleSlotLine = useMemo(() => {
    const ymd = appointmentDateYmd(formData.rDate);
    return [
      ymd ? formatSlotDate(ymd) : "",
      `${formatTimeHm(formData.rStartTime)}–${formatTimeHm(formData.rEndTime)}`,
      doctorDisplayName !== "—" ? doctorDisplayName : "",
      hasSessionQueue && effectiveSessionName ? effectiveSessionName : "",
      String(formData.roomNumber ?? "").trim()
        ? `Room ${String(formData.roomNumber).trim()}`
        : "",
    ]
      .filter(Boolean)
      .join(" · ");
  }, [
    rescheduleDate,
    formData.rStartTime,
    formData.rEndTime,
    doctorDisplayName,
    hasSessionQueue,
    effectiveSessionName,
    formData.roomNumber,
  ]);

  const rescheduleTitle = isTrayConfirmStep
    ? "Confirm reschedule"
    : isInPlaceVisitEdit
      ? "Edit visit"
      : "Reschedule appointment";

  const rescheduleTrayHint =
    trayProgress != null
      ? `Patient ${trayProgress.current} of ${trayProgress.total} in tray`
      : null;

  useEffect(() => {
    if (selectedOption !== "Reschedule") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedOption, handleCancel, handleSave]);

  if (selectedOption === "Reschedule") {
    return (
      <AppointmentModalOverlay
        role="dialog"
        aria-modal="true"
        aria-labelledby="appointment-schedule-title"
      >
        <RescheduleAppointmentShell
          title={rescheduleTitle}
          slotLine={rescheduleSlotLine}
          trayHint={rescheduleTrayHint}
          loading={appointmentLoading}
          saving={lodingBtn}
          patientName={formData?.name || "Patient"}
          patientPhone={formData?.mobileNo}
          purposes={purposes}
          formData={formData}
          setformData={setformData}
          purposeError={error}
          setPurposeError={seterror}
          pickDuration={pickRescheduleDuration}
          durationChipOptions={rescheduleDurationChipOptions}
          onEndTimeChange={handleRescheduleEndTime}
          formNextStart={formNextStart}
          hasSession={hasSessionQueue}
          sessionName={effectiveSessionName}
          roomNumber={String(formData.roomNumber ?? "").trim()}
          onQueueChange={handleChange}
          showScheduleFields={!isCalendarSlotLocked && !isInPlaceVisitEdit}
          lockedSchedule={lockedScheduleForForm}
          doctor={doctor}
          onFieldChange={handleChange}
          isCalendarSlotLocked={isCalendarSlotLocked}
          scheduleDate={rescheduleDate}
          onDateChange={(date) => {
            if (!date) return;
            handleChange({
              target: { value: appointmentDateYmd(date), name: "rDate" },
            });
          }}
          onFindNextSlot={!isCalendarSlotLocked && !isTrayReschedule ? handleMoveToNext : undefined}
          onBackToEdit={handleBackFromEdit}
          showBack={isInPlaceVisitEdit && !appointmentDetail?.fromRescheduleTray}
          cancelLabel={cancelButtonLabel}
          saveLabel={saveButtonLabel}
          onCancel={handleCancel}
          onSubmit={handleSave}
        />
      </AppointmentModalOverlay>
    );
  }

  return (
    <AppointmentModalOverlay
      role="dialog"
      aria-modal="true"
      aria-labelledby="appointment-schedule-title"
    >
      <div className={`${UI.modalShell} max-w-lg`}>
        <header className={`${UI.modalHeader} py-3`}>
          <div className="min-w-0 flex-1">
            <h2
              id="appointment-schedule-title"
              className="text-base font-semibold text-slate-900 tracking-tight"
            >
              {headerTitle}
            </h2>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
            title="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </header>

        <div className={`${UI.modalBody} py-3 space-y-3`}>
          {appointmentLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-slate-500">
              <Hourglass
                visible
                height="32"
                width="32"
                ariaLabel="loading-appointment"
                colors={["#6366f1", "#6366f1"]}
              />
              <p className="text-sm font-medium">Loading appointment…</p>
            </div>
          ) : (
            <>
          <VisitHero
            name={formData?.name || "Patient"}
            initial={formData?.name?.charAt(0)?.toUpperCase() || "?"}
            phone={formData?.mobileNo}
            status={formData.appointmentStatus}
            statusEditable={statusEditable}
            statusSaving={statusSaving}
            onStatusChange={handleStatusDropdownChange}
            timeRange={timeSummaryDisplay}
            durationLabel={visitDurationLabel}
            smartDate={smartDate}
            doctor={doctorDisplayName}
            purpose={purposeDisplayName}
            sessionName={effectiveSessionName}
            room={String(formData.roomNumber ?? "").trim()}
            queue={queueDisplay}
            hasSession={hasSessionQueue}
            showEdit={showAppointmentSummary}
            onEdit={handleEditAppointment}
          />

          {showAppointmentSummary && (
            <div className="space-y-3">
              <p className="px-1 text-xs font-medium text-slate-500">
                What should happen next?
              </p>
              <div className="space-y-2">
                {actionOptions.map((option) => (
                  <ActionOption
                    key={option.value}
                    selected={selectedOption === option.value}
                    icon={option.icon}
                    label={option.label}
                    description={option.description}
                    accent={option.color}
                    onSelect={() => handleOptionChange(option.value)}
                  />
                ))}
              </div>
            </div>
          )}

          {selectedOption === "Schedule" && (
            <div className="space-y-3">
              <FormSection title="Visit" hint="What this appointment is for">
                <PurposeMultiSelect
                  purposes={purposes?.purpose}
                  value={formData?.purposeIds ?? []}
                  onChange={(ids) => {
                    seterror(false);
                    setformData((prev: any) => ({ ...prev, purposeIds: ids }));
                  }}
                  error={error}
                />
                {error ? (
                  <p className="text-xs text-red-500">Please select at least one purpose</p>
                ) : null}
                <label className="flex items-center gap-2.5 pt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    name="walkIn"
                    checked={Boolean(formData.walkIn)}
                    onChange={(e) =>
                      setformData((prev: any) => ({
                        ...prev,
                        walkIn: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30"
                  />
                  <span className="text-sm font-medium text-slate-800">Walk-in</span>
                </label>
              </FormSection>

              <FormSection title="Schedule" hint="When and with whom">
                <div className="space-y-2">
                  <label className={UI.label}>Doctor</label>
                  <select
                    name="sDoctorId"
                    value={formData?.sDoctorId}
                    onChange={handleChange}
                    disabled
                    className={UI.inputDisabled}
                  >
                    {doctor?.map((item: any, i: number) => (
                      <option key={i} value={item?._id}>
                        Dr. {item?.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className={UI.label}>Date</label>
                  <div className={UI.readOnly}>{formatSmartDate(formData.sDate)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    name="sStartTime"
                    value={formData.sStartTime}
                    disabled
                    className={`flex-1 ${UI.inputDisabled}`}
                  />
                  <span className="text-xs text-slate-400">–</span>
                  <input
                    type="time"
                    name="sEndTime"
                    value={formData.sEndTime}
                    onChange={handleChange}
                    max={formNextStart ?? undefined}
                    className={`flex-1 ${UI.input}`}
                  />
                </div>
              </FormSection>
            </div>
          )}

            </>
          )}
        </div>

        <footer className={`${UI.modalFooter} py-3`}>
          {showAppointmentSummary && selectedActionLabel ? (
            <p className="mb-3 text-center text-xs text-slate-500">
              Selected: <span className="font-medium text-slate-700">{selectedActionLabel}</span>
            </p>
          ) : null}
          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={handleCancel}
              disabled={appointmentLoading}
              className={`${UI.btnSecondary} flex-1 py-2.5 disabled:opacity-50`}
            >
              {cancelButtonLabel}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saveDisabled || appointmentLoading}
              className={`${UI.btnPrimary} flex-1 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {lodingBtn ? (
                <>
                  <Hourglass
                    visible={true}
                    height="20"
                    width="20"
                    ariaLabel="hourglass-loading"
                    colors={["#fff", "#fff"]}
                  />
                  {isTrayConfirmStep ? "Confirming…" : "Saving..."}
                </>
              ) : (
                <>
                  <SaveIcon sx={{ fontSize: 20 }} />
                  {saveButtonLabel}
                </>
              )}
            </button>
          </div>
        </footer>
      </div>
    </AppointmentModalOverlay>
  );
}

export default AppointmentsSchedule;
